import React, { useCallback, useRef, memo, useEffect } from 'react';
import type { FunctionComponent } from 'react';
import './LongTextHover.scss';

interface LongNameHoverParams {
	text: string;
}

export const hideTooltips = () => document.dispatchEvent(new Event('hide-tooltips'));

export const LongTextHover: FunctionComponent<LongNameHoverParams> = memo(({ text }) => {
	const tooltipRef = useRef(document.createElement('div'));
	tooltipRef.current.classList.add('long-text-hover__tooltip');
	tooltipRef.current.innerText = text;

	const hoverTextRef = useRef<HTMLDivElement | null>(null);
	const textRef = useRef<HTMLSpanElement | null>(null);

	const isTextTruncated = useCallback(() => {
		if (!textRef.current || !hoverTextRef.current) return false;
		return textRef.current.offsetWidth > hoverTextRef.current?.offsetWidth;
	}, []);

	const onMouseOver = useCallback(() => {
		if (!hoverTextRef.current || !textRef.current) return;
		if (!isTextTruncated()) return;

		if (tooltipRef.current.parentNode !== document.body) {
			document.body.appendChild(tooltipRef.current);
		}

		const hoverText = hoverTextRef.current;
		const tooltip = tooltipRef.current;
		const hoverTextRect = hoverText.getBoundingClientRect();
		const tooltipRect = tooltip.getBoundingClientRect();

		let top = hoverTextRect.top - tooltipRect.height;

		if (top < 0) {
			top = hoverTextRect.bottom;
		}

		const left = hoverTextRect.left;

		tooltipRef.current.style.top = `${top}px`;
		tooltipRef.current.style.left = `${left}px`;
	}, [isTextTruncated]);

	const hideTooltip = useCallback(() => {
		if (tooltipRef.current.parentNode === document.body) {
			document.body.removeChild(tooltipRef.current);
		}
	}, []);

	useEffect(() => {
		document.addEventListener('hide-tooltips', hideTooltip, false);
		return () => document.removeEventListener('hide-tooltips', hideTooltip, false);
	});

	return (
		<div
			ref={hoverTextRef}
			className="long-text-hover"
			id="test"
			onMouseOver={onMouseOver}
			onMouseLeave={hideTooltip}
		>
			<span ref={textRef}>{text}</span>
		</div>
	);
});
